import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";
import type {
  AddParticipantListRequest,
  ParticipantListResponse,
  SendParticipantListToProvidersResponse,
  SendToProvidersRequest,
} from "./participantListApi";
import {
  addParticipantListToOrder,
  addParticipantListWithSignedLink,
  getParticipantListsForOrder,
  getParticipantListsWithSignedLink,
  sendParticipantListToProviders,
  updateParticipantListStatus,
  uploadParticipantListSpreadsheet,
  uploadParticipantListSpreadsheetWithSignedLink,
} from "./participantListApi";
import type { ParticipantListStatus } from "./participantListStatus";

export interface UseParticipantListsOptions {
  readonly signedLinkToken?: string;
}

function participantListQueryKey(orderId: string, signedLinkToken: string | undefined) {
  return signedLinkToken
    ? ["orders", orderId, "participant-lists", "signed-link", signedLinkToken]
    : ["orders", orderId, "participant-lists"];
}

export const useParticipantLists = (orderId: string, options: UseParticipantListsOptions = {}) => {
  const signedLinkToken = options?.signedLinkToken;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useQuery({
    queryKey: participantListQueryKey(orderId, signedLinkToken),
    queryFn: ({ signal }) =>
      signedLinkToken
        ? getParticipantListsWithSignedLink(orderId, signedLinkToken, signal)
        : getParticipantListsForOrder(orderId, signal),
  });

  const addParticipantListMutation = useMutation<
    ParticipantListResponse,
    unknown,
    {
      orderId: string;
      request: AddParticipantListRequest;
    }
  >({
    mutationFn: ({ orderId, request }) =>
      signedLinkToken
        ? addParticipantListWithSignedLink(orderId, signedLinkToken, request)
        : addParticipantListToOrder(orderId, request),
    onSuccess: async () => {
      enqueueSnackbar("Lista uczestników została dodana", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: participantListQueryKey(orderId, signedLinkToken) });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać listy uczestników. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateStatusMutation = useMutation<
    ParticipantListResponse,
    unknown,
    { listVersionNumber: number; status: ParticipantListStatus }
  >({
    mutationFn: ({ listVersionNumber, status }) => updateParticipantListStatus(orderId, listVersionNumber, status),
    onSuccess: (updatedList) => {
      queryClient.setQueryData<ParticipantListResponse[]>(
        participantListQueryKey(orderId, signedLinkToken),
        (prev) => prev?.map((item) => (item.id === updatedList.id ? updatedList : item)) ?? [],
      );
      enqueueSnackbar("Status listy uczestników został zmieniony", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zmienić statusu listy uczestników. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const uploadSpreadsheetMutation = useMutation<ParticipantListResponse, unknown, { orderId: string; file: File }>({
    mutationFn: ({ orderId, file }) =>
      signedLinkToken
        ? uploadParticipantListSpreadsheetWithSignedLink(orderId, signedLinkToken, file)
        : uploadParticipantListSpreadsheet(orderId, file),
    onSuccess: async () => {
      enqueueSnackbar("Lista uczestników została zaimportowana z pliku", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: participantListQueryKey(orderId, signedLinkToken) });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaimportować listy uczestników. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const sendToProvidersMutation = useMutation<
    SendParticipantListToProvidersResponse,
    unknown,
    { listVersionNumber: number; body: SendToProvidersRequest }
  >({
    mutationFn: ({ listVersionNumber, body }) => sendParticipantListToProviders(orderId, listVersionNumber, body),
    onSuccess: (result) => {
      queryClient.setQueryData<ParticipantListResponse[]>(
        participantListQueryKey(orderId, signedLinkToken),
        (prev) => prev?.map((item) => (item.id === result.participantList.id ? result.participantList : item)) ?? [],
      );
      const sentCount = result.sendResults.filter((r) => r.outcome === "SENT").length;
      const message = `Lista wysłana do ${sentCount} ${sentCount === 1 ? "dostawcy" : "dostawców"}`;
      enqueueSnackbar(message, { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się wysłać listy uczestników. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    participantLists: data ? sortBy(data, (l) => l.createdAt) : [],
    isLoading,
    addParticipantList: (request: AddParticipantListRequest) =>
      addParticipantListMutation.mutateAsync({
        orderId,
        request,
      }),
    uploadSpreadsheet: (file: File) => uploadSpreadsheetMutation.mutateAsync({ orderId, file }),
    updateStatus: (listVersionNumber: number, status: ParticipantListStatus) =>
      updateStatusMutation.mutateAsync({ listVersionNumber, status }),
    sendToProviders: (listVersionNumber: number, body: SendToProvidersRequest) =>
      sendToProvidersMutation.mutateAsync({ listVersionNumber, body }),
  };
};
